import type { DeepLink, FollowupStateGroup } from '@ops/contracts'
import { FOLLOWUP_STATE_GROUP_LABELS } from '@ops/contracts'

import { cx } from '../lib/cx'
import { Button } from './Button'
import { ExternalLink } from './ExternalLink'

/**
 * Chip de un registro que vive en otro sistema: `OPS-142 · En curso` que abre su
 * work item en Plane, `Armytech · lead` que abre su tarjeta en Close.
 *
 * Construido sobre `ExternalLink`, así que hereda la regla completa: pestaña
 * nueva, aviso accesible y, cuando falta el id, el estado "sin vincular" en vez
 * de un link roto.
 *
 * `rotulo` es el injerto de cobertura del briefing: **"Ya tiene seguimiento:"**
 * antes del chip cuando existe, y el botón de crear cuando no. De un golpe se ve
 * qué frentes están huérfanos.
 */
export interface RecordChipProps {
  /** El deep link al registro. `null` = el registro no existe todavía. */
  link: DeepLink | null
  /** Id legible del registro en su sistema, ej. "OPS-142". */
  identificador?: string | null
  /** Estado tal como lo llama el sistema de origen. El panel no lo traduce. */
  estado?: string | null
  /** Agrupación normalizada del estado: define el tono del chip. */
  estadoGroup?: FollowupStateGroup | null
  /** Rótulo explícito antes del chip, ej. "Ya tiene seguimiento:". */
  rotulo?: string
  /** Rótulo cuando no hay registro, ej. "Sin seguimiento". */
  rotuloVacio?: string
  /** Acción de crear el registro en el sistema de origen. */
  onCrear?: () => void
  textoCrear?: string
  className?: string
}

/** Tono por grupo de estado. El estado también viaja como texto en el chip. */
const TONO: Record<FollowupStateGroup, string> = {
  todo: 'border-n300 bg-n100 text-text',
  in_progress: 'border-warning bg-warning-soft text-warning-text',
  done: 'border-accent bg-accent-soft text-accent-text',
  cancelled: 'border-danger bg-danger-soft text-danger-text',
}

export function RecordChip({
  link,
  identificador,
  estado,
  estadoGroup,
  rotulo,
  rotuloVacio = 'Sin seguimiento',
  onCrear,
  textoCrear = 'Crear seguimiento',
  className,
}: RecordChipProps) {
  if (link === null) {
    return (
      <span
        data-testid="record-chip-vacio"
        className={cx(
          'inline-flex max-w-full flex-wrap items-center gap-1.5 text-micro text-muted-strong',
          className,
        )}
      >
        <span>{rotuloVacio}</span>
        {onCrear !== undefined ? (
          <Button variante="chico" icono="mas" onClick={onCrear}>
            {textoCrear}
          </Button>
        ) : null}
      </span>
    )
  }

  const etiquetaEstado =
    estado ??
    (estadoGroup !== null && estadoGroup !== undefined
      ? FOLLOWUP_STATE_GROUP_LABELS[estadoGroup]
      : null)

  return (
    <span
      data-testid="record-chip"
      data-estado-group={estadoGroup ?? undefined}
      className={cx(
        'inline-flex max-w-full flex-wrap items-center gap-1.5 text-micro',
        className,
      )}
    >
      {rotulo !== undefined ? (
        <span className="text-muted-strong">{rotulo}</span>
      ) : null}
      <ExternalLink
        link={link}
        variante="chip"
        className={cx(
          estadoGroup !== null && estadoGroup !== undefined
            ? TONO[estadoGroup]
            : undefined,
        )}
      >
        <span
          className={cx(
            'font-bold tabular',
            estadoGroup === 'cancelled' && 'line-through',
          )}
        >
          {identificador ?? link.label}
        </span>
        {etiquetaEstado !== null ? (
          <span className="ml-1 border-l border-border pl-1.5 font-medium">
            {etiquetaEstado}
          </span>
        ) : null}
      </ExternalLink>
    </span>
  )
}
